@if (isAvailableForForecast() && (!price() || !cost() || price()?.isLoading || cost()?.isLoading)) {
  <app-supplier-contract-card-skeleton />
} @else {
  <div
    class="rounded-lg border border-outline dark:border-outline-darkmode h-full w-full flex flex-col overflow-hidden"
    [ngClass]="[isEnded() ? 'bg-surface-variant dark:bg-surface-variant-darkmode' : '']"
  >
    @if (price()?.error || cost()?.error) {
      <div class="flex flex-col gap-2 items-center h-full w-full">
        <div class="flex-1 flex items-center justify-center">
          <app-error-component [enableRetry]="true" (clickRetry)="onClickRefresh()">
            @if (priceErrorStatus() === 400 || costErrorStatus() === 400) {
              {{ 'cockpit.supplierContracts.errors.pricesCalculating' | transloco }}<br />{{
                'cockpit.supplierContracts.errors.tryAgainLater' | transloco
              }}
            } @else if (priceErrorStatus() === 404 || costErrorStatus() === 404) {
              {{ 'cockpit.supplierContracts.errors.invalidContract' | transloco }}
            }
          </app-error-component>
        </div>
        <div class="pt-5 pb-4 px-6 flex-none w-full">
          <ui-button mode="outlined" expand="full" [routerLink]="['.', supplierContract().id]">
            {{ 'cockpit.supplierContracts.card.openContractButton' | transloco }}
            <ng-icon name="heroArrowRight" size="20" slot="end" class="!block" />
          </ui-button>
        </div>
      </div>
    } @else {
      <div
        class="py-5 px-6 flex-none flex gap-2 items-center"
        [ngClass]="
          isEnded()
            ? 'bg-surface-variant dark:bg-surface-variant-darkmode'
            : {
                ELECTRICITY: 'bg-power-soft dark:bg-power-soft-darkmode',
                GAS: 'bg-gas-soft dark:bg-gas-soft-darkmode',
              }[supplierContract().utilityType]
        "
      >
        <div
          class="flex-none p-2 rounded-lg border"
          [ngClass]="
            isEnded()
              ? 'border-outline dark:border-outline-darkmode text-on-surface-disabled dark:text-on-surface-disabled-darkmode'
              : {
                  ELECTRICITY:
                    'border-power-soft-outline dark:border-power-soft-outline-darkmode text-on-power-soft dark:text-on-power-soft-darkmode',
                  GAS: 'border-gas-soft-outline dark:border-gas-soft-outline-darkmode text-on-gas-soft dark:text-on-gas-soft-darkmode',
                }[supplierContract().utilityType]
          "
        >
          @switch (supplierContract().utilityType) {
            @case ('ELECTRICITY') {
              <ng-icon name="heroBolt" size="24" class="!block size-6" />
            }
            @case ('GAS') {
              <ng-icon name="heroFire" size="24" class="!block size-6" />
            }
          }
        </div>
        <div class="flex-1">
          <h2 class="font-semibold leading-5 line-clamp-1 break-all">
            {{ supplierContract().supplierName }} {{ supplierContract().productName }}
          </h2>
          @if (supplierContract().supplierContractID || supplierContract().supplierCustomerID) {
            <span
              class="text-sm text-on-surface-variant dark:text-on-surface-variant-darkmode line-clamp-1 break-all"
            >
              {{ supplierContract().supplierContractID || supplierContract().supplierCustomerID }}
            </span>
          }
        </div>
        @if (showFutureBadge()) {
          <ui-badge>{{ 'cockpit.supplierContracts.card.statusUpcoming' | transloco }}</ui-badge>
        } @else if (isEnded()) {
          <ui-badge>{{ 'cockpit.supplierContracts.card.statusEnded' | transloco }}</ui-badge>
        }
      </div>
      @if (contractCheckAlerts()?.alertCount; as count) {
        <div class="h-[0px] flex justify-center">
          <a
            [routerLink]="['.', supplierContract().id, 'check']"
            [title]="'cockpit.supplierContracts.card.checkAlerts.title' | transloco"
            class="mt-[-12.5px] relative block"
          >
            <ui-badge mode="filled" color="error" rounded="full">
              @if (count === 1) {
                {{ 'cockpit.supplierContracts.card.checkAlerts.one' | transloco }}
              } @else {
                {{ count }} {{ 'cockpit.supplierContracts.card.checkAlerts.other' | transloco }}
              }
            </ui-badge>
          </a>
        </div>
      }
      <div class="flex-1 p-6 border-t border-outline dark:border-outline-darkmode">
        <div class="flex flex-col gap-4 text-sm">
          <div class="flex gap-1">
            <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {{ 'cockpit.supplierContracts.card.locationsLabel' | transloco }}
            </div>
            <div
              class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap inline-flex gap-2"
            >
              @if (locationsLoading()) {
                <ui-skeleton class="block w-50 h-6 rounded-md" />
              } @else {
                <ui-badge class="flex-1">
                  <span class="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">
                    {{ locations()[0] | formatLocationName }}
                  </span>
                </ui-badge>
                @if (locations().length > 1) {
                  <ui-badge class="flex-none">+{{ locations().length - 1 }}</ui-badge>
                }
              }
            </div>
          </div>
          <div class="flex gap-1">
            <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {{ 'cockpit.supplierContracts.card.connectionsLabel' | transloco }}
            </div>
            <div
              class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap inline-flex gap-2"
            >
              @if (energyInterfacesLoading()) {
                <ui-skeleton class="block w-50 h-6 rounded-md" />
              } @else {
                <ui-badge class="flex-1">
                  <span class="tabular-nums">
                    {{
                      energyInterfaces()[0]?.maloID ||
                        energyInterfaces()[0]?.label ||
                        ('cockpit.supplierContracts.card.unknownMaloId' | transloco)
                    }}
                  </span>
                </ui-badge>
                @if (energyInterfaces().length > 1) {
                  <ui-badge class="flex-none">
                    <span class="tabular-nums"> +{{ energyInterfaces().length - 1 }} </span>
                  </ui-badge>
                }
              }
            </div>
          </div>
          <div class="flex gap-1">
            <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {{ 'cockpit.supplierContracts.card.contractStartLabel' | transloco }}
            </div>
            <div
              class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap"
            >
              <ui-badge>
                {{ supplierContract().contractStartDate | date: 'dd.MM.yyyy' }}
              </ui-badge>
            </div>
          </div>
          @if (supplierContract().contractEndDate) {
            <div class="flex gap-1">
              <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {{ 'cockpit.supplierContracts.card.contractEndLabel' | transloco }}
              </div>
              <div
                class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap"
              >
                <ui-badge>
                  {{ supplierContract().contractEndDate | date: 'dd.MM.yyyy' }}
                </ui-badge>
              </div>
            </div>
          } @else {
            <div class="flex gap-1">
              <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {{ 'cockpit.supplierContracts.card.terminationPeriodLabel' | transloco }}
              </div>
              <div
                class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap"
              >
                <ui-badge>
                  {{ latestPossibleTerminationDate() | date: 'dd.MM.yyyy' }}
                </ui-badge>
              </div>
            </div>
          }
          <hr class="border-outline dark:border-outline-darkmode" />

          <div class="flex gap-1">
            <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {{ 'cockpit.supplierContracts.card.annualConsumptionLabel' | transloco }}
            </div>
            <div
              class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap"
            >
              @if (isPast()) {
                <ui-badge
                  [uiTooltip]="'cockpit.supplierContracts.card.noForecastPast' | transloco"
                  class="cursor-default"
                  >-</ui-badge
                >
              } @else if (!isAvailableForForecast()) {
                <ui-badge
                  color="soft"
                  [uiTooltip]="'cockpit.supplierContracts.card.noForecastFuture' | transloco"
                  class="cursor-default"
                  >{{ 'cockpit.supplierContracts.card.tooFarInFuture' | transloco }}</ui-badge
                >
              } @else if (price()?.isLoading && cost()?.isLoading) {
                <ui-skeleton class="block w-30 h-6 rounded-md" />
              } @else {
                <ui-badge>
                  <span class="tabular-nums">
                    {{ totalConsumption() | germanNumber: 0 }}
                    {{ 'cockpit.supplierContracts.units.referenceUnit.KWH' | transloco }}
                  </span>
                </ui-badge>
              }
            </div>
          </div>
          <div class="flex gap-1">
            <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {{ 'cockpit.supplierContracts.card.consumptionPriceLabel' | transloco }}
            </div>
            <div
              class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap"
            >
              @if (isPast()) {
                <ui-badge
                  [uiTooltip]="'cockpit.supplierContracts.card.noForecastPast' | transloco"
                  class="cursor-default"
                  >-</ui-badge
                >
              } @else if (!isAvailableForForecast()) {
                <ui-badge
                  color="soft"
                  [uiTooltip]="'cockpit.supplierContracts.card.noForecastFuture' | transloco"
                  class="cursor-default"
                  >{{ 'cockpit.supplierContracts.card.tooFarInFuture' | transloco }}</ui-badge
                >
              } @else if (price()?.isLoading) {
                <ui-skeleton class="block w-25 h-6 rounded-md" />
              } @else if (supplierContract().consumptionPriceScoreOverride; as score) {
                <ui-badge mode="dot" [color]="score | scoreColor">
                  <span class="tabular-nums">
                    {{
                      (fullPrice() ? totalPriceWithTaxes() : totalPriceWithoutTaxes())
                        | priceRange: 'CENTS' : 2
                    }}
                    {{ 'cockpit.supplierContracts.units.currencyUnit.CENTS' | transloco }}
                  </span>
                </ui-badge>
              } @else {
                <ui-badge>
                  <span class="tabular-nums">
                    {{
                      (fullPrice() ? totalPriceWithTaxes() : totalPriceWithoutTaxes())
                        | priceRange: 'CENTS' : 2
                    }}
                    {{ 'cockpit.supplierContracts.units.currencyUnit.CENTS' | transloco }}
                  </span>
                </ui-badge>
              }
            </div>
          </div>
          <div class="flex gap-1">
            <div class="font-semibold flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              {{
                'cockpit.supplierContracts.card.energyCostsLabel'
                  | transloco: { year: currentYear() }
              }}
            </div>
            <div
              class="text-on-surface-subtle dark:text-on-surface-subtle-darkmode overflow-hidden text-ellipsis whitespace-nowrap"
            >
              @if (isPast()) {
                <ui-badge
                  [uiTooltip]="'cockpit.supplierContracts.card.noForecastPast' | transloco"
                  class="cursor-default"
                  >-</ui-badge
                >
              } @else if (!isAvailableForForecast()) {
                <ui-badge
                  color="soft"
                  [uiTooltip]="'cockpit.supplierContracts.card.noForecastFuture' | transloco"
                  class="cursor-default"
                  >{{ 'cockpit.supplierContracts.card.tooFarInFuture' | transloco }}</ui-badge
                >
              } @else if (cost()?.isLoading) {
                <ui-skeleton class="block w-30 h-6 rounded-md" />
              } @else if (supplierContract().costScoreOverride; as score) {
                <ui-badge mode="dot" [color]="score | scoreColor">
                  <span class="tabular-nums">
                    {{
                      (fullPrice() ? totalEnergyCostWithTaxes() : totalEnergyCostWithoutTaxes())
                        | priceRange: 'EURO'
                    }}
                    {{ 'cockpit.supplierContracts.units.currencyUnit.EURO_SYMBOL' | transloco }}
                  </span>
                </ui-badge>
              } @else {
                <ui-badge>
                  <span class="tabular-nums">
                    {{
                      (fullPrice() ? totalEnergyCostWithTaxes() : totalEnergyCostWithoutTaxes())
                        | priceRange: 'EURO'
                    }}
                    {{ 'cockpit.supplierContracts.units.currencyUnit.EURO_SYMBOL' | transloco }}
                  </span>
                </ui-badge>
              }
            </div>
          </div>
        </div>
      </div>
      <div class="pt-5 pb-4 px-6 flex-none">
        <ui-button mode="outlined" expand="full" [routerLink]="['.', supplierContract().id]">
          {{ 'cockpit.supplierContracts.card.openContractButton' | transloco }}
          <ng-icon name="heroArrowRight" size="20" slot="end" class="!block" />
        </ui-button>
      </div>
    }
  </div>
}
